import { Ellipsis } from '@/components/ui/ellipsis';
import { FlexCol } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

// Types for the table data
export interface DatabaseResult {
  workId: string;
  doi: string | null;
  title: string;
  authors: string;
  journal: string;
  publicationDate: Date | null;
}

export interface CompleteResult {
  workId: string;
  doi: string | null;
  title: string;
  authors: string;
  journal: string;
  publicationDate: Date | null;
  source: 'pubmed' | 'openalex' | 'unknown';
}

// Hook for database-specific table columns
export const useDatabaseColumns = (database: string) => {
  return useMemo<ColumnDef<DatabaseResult>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        meta: {
          headerClassName: 'min-w-[400px] max-w-[400px]',
          cellClassName: 'min-w-[400px] max-w-[400px]',
        },
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-center">
            <Text className="text-sm font-medium">{row.original.title}</Text>
            <Ellipsis lineClamp={3} hasTooltip asChild>
              <Text className="text-xs text-neutral-600">
                {row.original.authors}
              </Text>
            </Ellipsis>
          </FlexCol>
        ),
      },
      {
        accessorKey: 'journal',
        header: 'Journal',
        meta: {
          headerClassName: 'min-w-[130px] max-w-[130px]',
          cellClassName: 'min-w-[130px] max-w-[130px]',
        },
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm font-medium">{row.original.journal}</Text>
          </FlexCol>
        ),
      },
      {
        accessorKey: 'publicationDate',
        header: 'Publication Date',
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm font-medium">
              {row.original.publicationDate
                ? new Date(row.original.publicationDate).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                    }
                  )
                : 'N/A'}
            </Text>
          </FlexCol>
        ),
      },
      {
        id: 'source',
        header: 'Source',
        cell: () => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm capitalize font-medium">{database}</Text>
          </FlexCol>
        ),
      },
    ],
    [database]
  );
};

// Hook for complete results table columns
export const useCompleteResultsColumns = () => {
  return useMemo<ColumnDef<CompleteResult>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        meta: {
          headerClassName: 'min-w-[400px] max-w-[400px]',
          cellClassName: 'min-w-[400px] max-w-[400px]',
        },
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-center">
            <Text className="text-sm font-medium">{row.original.title}</Text>
            <Ellipsis lineClamp={3} hasTooltip asChild>
              <Text className="text-xs text-neutral-600">
                {row.original.authors}
              </Text>
            </Ellipsis>
          </FlexCol>
        ),
      },
      {
        accessorKey: 'journal',
        header: 'Journal',
        meta: {
          headerClassName: 'min-w-[130px] max-w-[130px]',
          cellClassName: 'min-w-[130px] max-w-[130px]',
        },
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm font-medium">{row.original.journal}</Text>
          </FlexCol>
        ),
      },
      {
        accessorKey: 'publicationDate',
        header: 'Publication Date',
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm font-medium">
              {row.original.publicationDate
                ? new Date(row.original.publicationDate).toLocaleDateString(
                    'en-US',
                    {
                      year: 'numeric',
                      month: 'long',
                    }
                  )
                : 'N/A'}
            </Text>
          </FlexCol>
        ),
      },
      {
        accessorKey: 'source',
        header: 'Source',
        cell: ({ row }) => (
          <FlexCol className="gap-2 px-2 py-4 items-start justify-start h-full">
            <Text className="text-sm capitalize font-medium">
              {row.original.source}
            </Text>
          </FlexCol>
        ),
      },
    ],
    []
  );
};
